import {createContext, useCallback, useContext, useState} from 'react'
import type {ReactNode} from 'react'

/* WHY: pages and the inner PageLayout/UniversalHeader both call usePageTitle.
   Effect order is bottom-up (child first, parent last), so the outer page
   call wins and overrides PageLayout's entity-specific title. Splitting the
   slot in two — `highTitle` set by PageLayout, `lowTitle` set by anyone else
   — gives PageLayout deterministic priority regardless of effect ordering. */
interface PageTitleContextValue {
    title: string
    setTitle: (next: string) => void
    setHighPriorityTitle: (next: string) => void
}

const PageTitleContext = createContext<PageTitleContextValue | null>(null)

export function PageTitleProvider({children}: {children: ReactNode}) {
    const [lowTitle, setLowTitle] = useState('')
    const [highTitle, setHighTitle] = useState('')
    const setTitle = useCallback((next: string) => setLowTitle(next), [])
    const setHighPriorityTitle = useCallback((next: string) => setHighTitle(next), [])
    const title = highTitle || lowTitle
    return (
        <PageTitleContext.Provider value={{title, setTitle, setHighPriorityTitle}}>
            {children}
        </PageTitleContext.Provider>
    )
}

export function usePageTitleContext(): PageTitleContextValue {
    const ctx = useContext(PageTitleContext)
    if (!ctx) {
        /* WHY: when the context isn't mounted (early tests without the
           AppShell wrappers) usePageTitle should silently no-op rather
           than crash. The title we'd write is irrelevant in that case. */
        return {title: '', setTitle: () => {}, setHighPriorityTitle: () => {}}
    }
    return ctx
}
